import { api } from '@/api/axios';

/**
 * GET an axios endpoint as a blob and trigger a browser download.
 * The Authorization header is attached automatically by the shared api instance,
 * so we can hit auth-gated export endpoints without leaking JWTs into URLs.
 */
export async function downloadFile(
  url: string,
  params: Record<string, unknown>,
  fileName: string,
): Promise<void> {
  const response = await api.get<Blob>(url, {
    params,
    responseType: 'blob',
  });
  const blobUrl = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke the object URL on the next tick so the click() has time to register.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
}

/**
 * GET an auth-gated PDF as a blob and open it inline in a new tab.
 * The blank tab is opened synchronously (still inside the user gesture) so
 * popup blockers don't eat it while the fetch is in flight.
 */
export async function openPdf(
  url: string,
  params: Record<string, unknown> = {},
): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const response = await api.get<Blob>(url, { params, responseType: 'blob' });
    const blobUrl = URL.createObjectURL(
      new Blob([response.data], { type: 'application/pdf' }),
    );
    if (tab) tab.location.href = blobUrl;
    else window.open(blobUrl, '_blank');
    // Keep the object URL alive long enough for the tab to load the PDF.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}
